import { useEffect, useRef, useState } from "react";

export type DateRangeKey = "today" | "yesterday" | "7d" | "30d" | "month" | "custom";

interface DateRangeSelectorProps {
  value: DateRangeKey;
  onChange: (value: DateRangeKey) => void;
  customStart: string;
  customEnd: string;
  onCustomChange: (start: string, end: string) => void;
}

const OPTIONS: { key: DateRangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "month", label: "This Month" },
  { key: "custom", label: "Custom Range" },
];

export default function DateRangeSelector({
  value,
  onChange,
  customStart,
  customEnd,
  onCustomChange,
}: DateRangeSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const active = OPTIONS.find((option) => option.key === value) || OPTIONS[3];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex items-center gap-2 bg-background-50 border border-background-200 text-foreground-700 text-sm px-4 py-2.5 rounded-md hover:border-background-300 transition-colors whitespace-nowrap cursor-pointer"
      >
        <i className="ri-calendar-line text-primary-600"></i>
        {active.label}
        <i className="ri-arrow-down-s-line text-foreground-400"></i>
      </button>

      {open ? (
        <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-lg overflow-hidden py-1.5 z-40">
          {OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                onChange(option.key);
                if (option.key !== "custom") setOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors cursor-pointer ${
                value === option.key
                  ? "bg-primary-50 text-primary-800 font-semibold"
                  : "text-foreground-800 hover:bg-background-100"
              }`}
            >
              {option.label}
              {value === option.key ? <i className="ri-check-line text-primary-600"></i> : null}
            </button>
          ))}

          {value === "custom" ? (
            <div className="px-4 py-3 border-t border-background-200 mt-1 space-y-2">
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">From</span>
                <input
                  type="date"
                  value={customStart}
                  onChange={(event) => onCustomChange(event.target.value, customEnd)}
                  className="mt-1 w-full bg-background-100 border border-background-300 rounded-md px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">To</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(event) => onCustomChange(customStart, event.target.value)}
                  className="mt-1 w-full bg-background-100 border border-background-300 rounded-md px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </label>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}